import React, { useState } from 'react';
import {
    FormHelperText,
    InputLabel,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import FormTextField from '../../../../shared/FormTextField';
import { useFormContext, useWatch } from 'react-hook-form';
import { z } from 'zod';
import FormSelect from '../../../../shared/FormSelect';
import { DetailsSchema } from './schema';
import { useGetCompanyOwnerInfoQuery } from '../../../../../app/Slices/SevenDayApiSlice';
import UserRole from '../../../../../shared/AuthRoles';

type FormValues = z.infer<typeof DetailsSchema>;

const ExperienceDetails = () => {
    const { control } = useFormContext<FormValues>();

    return (
        <fieldset>
            <Stack spacing={2}>
                <div>
                    <Typography variant='h2' component='legend'>
                        Experience Details
                    </Typography>
                    <Typography color='text.secondary' gutterBottom>
                        Tell guests about your experience
                    </Typography>
                </div>
                <div>
                    <InputLabel htmlFor='name__input'>Name</InputLabel>
                    <FormTextField
                        id='name__input'
                        name='name'
                        control={control}
                        inputProps={{ maxLength: 25 }}
                    />
                </div>
                <div>
                    <InputLabel htmlFor='description__input'>
                        Description
                    </InputLabel>
                    <FormTextField
                        id='description__input'
                        name='description'
                        control={control}
                        multiline
                        rows={4}
                        inputProps={{ maxLength: 500 }}
                    />
                    <FormHelperText>
                        A brief description (max 500 characters) describing what
                        this experience offers
                    </FormHelperText>
                </div>
                <div>
                    <InputLabel
                        htmlFor='visit-type__input'
                        id='visit-type__label'
                    >
                        Visit Type
                    </InputLabel>
                    <FormSelect
                        id='visit-type__input'
                        labelId='visit-type__label'
                        name='visitType'
                        control={control}
                        options={[
                            {
                                value: 'personal',
                                label: 'Personal',
                            },
                            {
                                value: 'business',
                                label: 'Business',
                            },
                        ]}
                    />
                </div>
                <div>
                    <InputLabel htmlFor='notifications-email__input'>
                        Notifications Email
                    </InputLabel>
                    <NotificationsEmailInput />
                    <FormHelperText>
                        This email will receive notifications for this
                        experience such as new, edited and cancelled
                        reservations
                    </FormHelperText>
                </div>
            </Stack>
        </fieldset>
    );
};

const NotificationsEmailInput = () => {
    const theme = useTheme();
    const loggedInUser = UserRole();

    const { data: owner } = useGetCompanyOwnerInfoQuery(loggedInUser?.company);

    const { control, setValue, clearErrors } = useFormContext<FormValues>();

    const notificationsEmail = useWatch({
        control,
        name: 'notificationsEmail',
    });

    const [type, setType] = useState<'default' | 'custom'>(
        notificationsEmail === '' ? 'default' : 'custom',
    );

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setType(e.target.value as 'default' | 'custom');

        if (e.target.value === 'default') {
            clearErrors('notificationsEmail');
            setValue('notificationsEmail', '');
        }
    };

    return (
        <Stack direction='row' alignItems='center'>
            <div style={{ flex: 1 }}>
                <FormTextField
                    id='notifications-email__input'
                    name='notificationsEmail'
                    control={control}
                    disabled={type === 'default'}
                    placeholder={
                        type === 'default' ? owner?.notificationsEmail : ''
                    }
                    InputProps={{
                        startAdornment: (
                            <select
                                id='notifications-email-type__input'
                                value={type}
                                onChange={handleTypeChange}
                                style={{
                                    marginRight: theme.spacing(1),
                                    border: 'none',
                                    background: 'inherit',
                                    fontFamily: theme.typography.fontFamily,
                                    fontSize: theme.typography.body2.fontSize,
                                    color: theme.palette.text.primary,
                                }}
                            >
                                <option value='default'>Default</option>
                                <option value='custom'>Custom</option>
                            </select>
                        ),
                    }}
                />
            </div>
        </Stack>
    );
};

export default ExperienceDetails;
